import { Button, SvgArrowDownward, SvgArrowUpward } from '@loomhq/lens';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';

import {
  requestVideoPlayerCurrentTime,
  requestVideoPlayerPlay,
  updateEmojiClickedInVideoPlayer,
  updateVideoPlayerPlaying,
} from 'actions/videoPlayer';
import {
  TRANSCRIPTION_BACK_TO_ACTIVE_SCROLLING_CLICK,
  TRANSCRIPTION_NAVIGATED_BY_PHRASE_CLICK,
  TRANSCRIPTION_NAVIGATED_BY_USER_SCROLL,
} from 'constants/events';
import * as analytics from 'utilities/analytics';

import EmptyTranscript from './emptyTranscript';
import RenderRowAtIndex from './transcript-row';
import {
  selectCurrentSearchIndex,
  selectIsTranscriptSearchOpen,
  selectTranscriptSearchPhrase,
  selectTranscriptSearchPhraseIndexArray,
} from '../../../selectors/shareVideo';
import {
  selectCurrentVideoId,
  selectEmojiClickedInVideoPlayer,
  selectHasStarted,
  selectPaused,
  selectPlayerTime,
} from '../../../selectors/videoPlayer';
import { findPhraseIndex } from '../../../utilities/transcriptList';

import styles from './styles.module.less';

const TOP = 'top';
const BOTTOM = 'bottom';
const UP = 'up';
const DOWN = 'down';

const TranscriptList = ({ transcript }) => {
  const dispatch = useDispatch();
  const { phrases } = transcript;
  const listRef = useRef(null);
  const rowHeights = useRef({});
  const currentPlayTime = useSelector(selectPlayerTime);
  const hasStarted = useSelector(selectHasStarted);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [userScrolled, setUserScrolled] = useState(false);
  const [buttonLocation, setButtonLocation] = useState(TOP);
  const [arrowDirection, setArrowDirection] = useState(UP);
  const [firstHookCall, setFirstHookCall] = useState(false);
  const [atEnd, setAtEnd] = useState(false);
  const paused = useSelector(selectPaused);
  const emojiClicked = useSelector(selectEmojiClickedInVideoPlayer);
  const [firstVisibleIndex, setFirstVisibleIndex] = useState(0);
  const isTranscriptSearchOpen = useSelector(selectIsTranscriptSearchOpen);
  const searchPhrase = useSelector(selectTranscriptSearchPhrase);
  const indexArray = useSelector(selectTranscriptSearchPhraseIndexArray);
  const searchIndex = useSelector(selectCurrentSearchIndex);
  const csIndex = indexArray[searchIndex - 1]?.index;
  const counter = indexArray[searchIndex - 1]?.counter;

  const currentUserIsOwner = useSelector(
    state => state.share_video.current_user_is_owner
  );

  const videoId = useSelector(selectCurrentVideoId);

  const getRowHeight = index => {
    return rowHeights.current[index] || 60;
  };

  const setRowHeight = (index, size) => {
    rowHeights.current[index] = size;
    listRef.current.resetAfterIndex(index ? index - 1 : index);
  };

  useEffect(() => {
    if (!phrases || phrases.length < 1) {
      return;
    }

    // update state to ensure that listRef.current has been set
    // to have listref.current scrollToItem if transcript is open
    // when video is paused
    if (!firstHookCall) {
      setFirstHookCall(true);
    }

    const calculatedIndex = findPhraseIndex(currentPlayTime, phrases);

    if (emojiClicked && listRef.current) {
      setCurrentIndex(calculatedIndex);

      if (!isTranscriptSearchOpen) {
        listRef.current.scrollToItem(calculatedIndex - 1, 'start');
      }

      setUserScrolled(false);
    } else if (calculatedIndex !== currentIndex && listRef.current) {
      setCurrentIndex(calculatedIndex);

      if (!userScrolled && !isTranscriptSearchOpen) {
        listRef.current.scrollToItem(calculatedIndex - 1, 'start');
      }
    }
  }, [
    currentPlayTime,
    currentIndex,
    setCurrentIndex,
    phrases,
    userScrolled,
    firstHookCall,
    emojiClicked,
    paused,
    dispatch,
    isTranscriptSearchOpen,
  ]);

  useEffect(() => {
    if (listRef.current && !isTranscriptSearchOpen && !userScrolled) {
      listRef.current.scrollToItem(currentIndex - 1, 'start');
    }
  }, [isTranscriptSearchOpen, currentIndex, userScrolled]);

  useEffect(() => {
    if (currentIndex < firstVisibleIndex + 2) {
      setButtonLocation(TOP);
      setArrowDirection(UP);
    } else {
      setButtonLocation(BOTTOM);
      setArrowDirection(DOWN);
    }
  }, [currentIndex, firstVisibleIndex]);

  useEffect(() => {
    if (listRef.current && isTranscriptSearchOpen && csIndex > -1) {
      listRef.current.scrollToItem(csIndex - 1, 'start');
    }
  }, [searchPhrase, csIndex, counter, isTranscriptSearchOpen]);

  const onRowsRendered = ({ visibleStopIndex, visibleStartIndex }) => {
    setAtEnd(visibleStopIndex === phrases.length - 1);

    if (visibleStartIndex !== firstVisibleIndex) {
      setFirstVisibleIndex(visibleStartIndex);
    }
  };

  const updateUserScrolled = ({ scrollOffset, scrollUpdateWasRequested }) => {
    if (
      !isTranscriptSearchOpen &&
      !scrollUpdateWasRequested &&
      !atEnd &&
      scrollOffset > 0 &&
      !emojiClicked
    ) {
      setUserScrolled(true);

      analytics.track(TRANSCRIPTION_NAVIGATED_BY_USER_SCROLL, {
        currentUserIsOwner,
        video_id: videoId,
      });
    }

    if (emojiClicked) {
      dispatch(updateEmojiClickedInVideoPlayer(false));
    }
  };

  const backToActiveScrolling = () => {
    listRef.current.scrollToItem(currentIndex - 1, 'start');
    setUserScrolled(false);

    analytics.track(TRANSCRIPTION_BACK_TO_ACTIVE_SCROLLING_CLICK, {
      currentUserIsOwner,
      video_id: videoId,
    });
  };

  const handleRowClick = index => {
    const selection = document.getSelection();
    const currentPhraseTs = phrases[index]['ts'];

    // if user has highlighted the phrase text rather
    // than clicked on a row then the selection type will be range
    // and we want to return rather than update the current player time
    if (selection.type === 'Range') {
      return;
    }

    setCurrentIndex(index);

    dispatch(requestVideoPlayerCurrentTime(currentPhraseTs));

    if (!hasStarted) {
      dispatch(updateVideoPlayerPlaying());
      dispatch(requestVideoPlayerPlay());
    }

    analytics.track(TRANSCRIPTION_NAVIGATED_BY_PHRASE_CLICK, {
      currentUserIsOwner,
      video_id: videoId,
    });
  };

  if (phrases === undefined) {
    return <EmptyTranscript failed={true} />;
  }

  if (phrases.length < 1) {
    return <EmptyTranscript empty={true} />;
  }

  return (
    <>
      <AutoSizer>
        {({ height, width }) => (
          <List
            height={height}
            itemCount={phrases.length}
            itemSize={getRowHeight}
            estimatedItemSize={height}
            width={width}
            ref={listRef}
            onScroll={updateUserScrolled}
            overscanCount={20}
            onItemsRendered={onRowsRendered}
          >
            {({ index, style }) =>
              RenderRowAtIndex({
                index,
                currentIndex,
                style,
                setRowHeight,
                phrases,
                handleRowClick,
                hasStarted,
                csIndex,
                counter,
              })
            }
          </List>
        )}
      </AutoSizer>
      {userScrolled &&
        hasStarted &&
        currentIndex !== phrases.length - 1 &&
        !isTranscriptSearchOpen && (
          <div
            className={classNames(
              styles.btnContainer,
              buttonLocation === TOP ? styles.top : styles.bottom
            )}
          >
            <Button
              variant="primary"
              icon={
                arrowDirection === UP ? (
                  <SvgArrowUpward />
                ) : (
                  <SvgArrowDownward />
                )
              }
              hasFullWidth
              onClick={backToActiveScrolling}
            >
              Back to current time
            </Button>
          </div>
        )}
    </>
  );
};

export default TranscriptList;
